import { FC, ReactNode, useCallback, useEffect, useState } from 'react'
import { Tooltip, TooltipProps } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import {
  resolveTooltipContent,
  TooltipContentKey,
  TooltipContentValue,
} from './tooltip.content'

export type InfoTooltipProps = {
  /**
   * Dot-path key into the centralized tooltip content object,
   * e.g. "poStatus.Pending". Typed to the set of keys that actually exist,
   * so an invalid key is a compile error. This is the ONLY way to supply text —
   * raw strings are intentionally not accepted so content stays centralized.
   */
  contentKey: TooltipContentKey
  /** Where the tooltip appears relative to its trigger. Defaults to 'top'. */
  placement?: TooltipProps['placement']
  /**
   * Optional custom trigger (e.g. a label hover area). When omitted, a subtle
   * info icon is used as the trigger — the preferred, consistent pattern.
   */
  children?: ReactNode
  /** Extra class for the default info-icon trigger. */
  className?: string
}

/** Render a single string, or a string[] as separate lines (multi-line). */
const renderContent = (content: TooltipContentValue): ReactNode => {
  if (Array.isArray(content)) {
    return (
      <span style={{ whiteSpace: 'pre-line' }}>
        {content.map((line, index) => (
          <span key={index} style={{ display: 'block' }}>
            {line}
          </span>
        ))}
      </span>
    )
  }
  return <span style={{ whiteSpace: 'pre-line' }}>{content}</span>
}

/**
 * Reusable tooltip for contextual help across the PoS.
 *
 * Behaviour:
 *  - Shows on hover (desktop) and on click/tap (mobile / fallback).
 *  - Hides on mouse leave, outside click, and the Escape key.
 *  - Flexible placement (top / right / bottom / left).
 *  - Auto-repositions near screen edges and renders in a portal (antd).
 *  - Long text wraps; multi-line content is supported via string[] values.
 *  - Missing/unknown key fails gracefully (renders nothing, never crashes).
 *
 * Content is resolved exclusively from the centralized tooltip.content object.
 */
export const InfoTooltip: FC<InfoTooltipProps> = ({
  contentKey,
  placement = 'top',
  children,
  className,
}) => {
  const [open, setOpen] = useState(false)

  // Escape key closes the tooltip while it is open (preferred behaviour).
  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  // antd drives open/close for hover, click and outside-click; we mirror it to
  // state so Escape can also close it. We never early-return before this hook.
  const handleOpenChange = useCallback((next: boolean) => setOpen(next), [])

  const content = resolveTooltipContent(contentKey)

  // Graceful fallback: unknown key → render the trigger without a tooltip
  // (or nothing, when using the default icon) so the UI never breaks.
  if (content === undefined) {
    return <>{children ?? null}</>
  }

  const trigger = children ?? (
    <InfoCircleOutlined
      className={className}
      style={{ color: 'rgba(0, 0, 0, 0.45)', cursor: 'pointer' }}
      tabIndex={0}
      aria-label="More information"
    />
  )

  return (
    <Tooltip
      title={renderContent(content)}
      placement={placement}
      open={open}
      onOpenChange={handleOpenChange}
      trigger={['hover', 'click']}
      autoAdjustOverflow
      overlayStyle={{ maxWidth: 280 }}
    >
      {trigger}
    </Tooltip>
  )
}
